import { Banner } from '@evisort/eds/components/Banner/Banner';
import { Button } from '@evisort/eds/components/Button/Button';
import { FormField } from '@evisort/eds/components/FormField/FormField';
import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Text } from '@evisort/eds/components/Text/Text';
import { useForm, useStore } from '@tanstack/react-form';
import { type FormEvent, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { changePassword } from '~/api/login';
import { AuthMarketingHeader } from '~/components/AuthPageLayout/AuthMarketingHeader';
import { AuthPageLayout } from '~/components/AuthPageLayout/AuthPageLayout';
import { completePasswordResetHandoff } from '~/components/LoginPage/loginHandoff';
import {
  isPasswordFullyValid,
  PasswordRequirementsList,
} from '~/components/PasswordRequirementsList/PasswordRequirementsList';
import { QueryParamType } from '~/enums/QueryParamType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormFocusManager } from '~/hooks/useFormFocusManager';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

import styles from './ChangePasswordPage.module.css';

const messages = defineMessages({
  confirmPasswordLabel: {
    id: 'ChangePasswordPage.confirmPasswordLabel',
    defaultMessage: 'Confirm Password',
    description: 'Label for the confirm new password input',
  },
  description: {
    id: 'ChangePasswordPage.description',
    defaultMessage: 'Choose a new password for your account.',
    description: 'Description under the change password heading',
  },
  genericErrorMessage: {
    id: 'ChangePasswordPage.genericErrorMessage',
    defaultMessage: 'An error occurred. Try again.',
    description: 'Generic error message when changing the password fails',
  },
  invalidLinkMessage: {
    id: 'ChangePasswordPage.invalidLinkMessage',
    defaultMessage:
      'This reset link is no longer valid. Request a new one to reset your password.',
    description: 'Message shown when the reset password link has no token',
  },
  invalidLinkTitle: {
    id: 'ChangePasswordPage.invalidLinkTitle',
    defaultMessage: 'Invalid Reset Link',
    description: 'Title shown when the reset password link has no token',
  },
  marketingSubtitle: {
    id: 'ChangePasswordPage.marketingSubtitle',
    defaultMessage:
      'Set a new password to get back to your contract intelligence platform.',
    description: 'Subtitle for the change password page marketing panel',
  },
  marketingTitle: {
    id: 'ChangePasswordPage.marketingTitle',
    defaultMessage: 'Welcome Back',
    description: 'Headline on the change password page marketing panel',
  },
  newPasswordLabel: {
    id: 'ChangePasswordPage.newPasswordLabel',
    defaultMessage: 'New Password',
    description: 'Label for the new password input',
  },
  passwordRequirementsError: {
    id: 'ChangePasswordPage.passwordRequirementsError',
    defaultMessage: "Your password doesn't meet the requirements below.",
    description: 'Error shown when the new password fails validation',
  },
  passwordsDontMatchError: {
    id: 'ChangePasswordPage.passwordsDontMatchError',
    defaultMessage: "Passwords don't match.",
    description: 'Error shown when the confirm password does not match',
  },
  requestNewLinkButton: {
    id: 'ChangePasswordPage.requestNewLinkButton',
    defaultMessage: 'Request New Link',
    description: 'Button to go to the forgot password page for a new link',
  },
  submitButton: {
    id: 'ChangePasswordPage.submitButton',
    defaultMessage: 'Reset Password',
    description: 'Button to submit the new password',
  },
  successMessage: {
    id: 'ChangePasswordPage.successMessage',
    defaultMessage:
      "Your password has been updated. You'll be redirected to the platform shortly.",
    description: 'Message shown after the password is changed',
  },
  successTitle: {
    id: 'ChangePasswordPage.successTitle',
    defaultMessage: 'Password Updated',
    description: 'Heading shown after the password is changed',
  },
  title: {
    id: 'ChangePasswordPage.title',
    defaultMessage: 'Create a New Password',
    description: 'Heading for the change password page',
  },
});

interface ChangePasswordFormValues {
  password: string;
  confirmedPassword: string;
}

interface ChangePasswordResponse {
  msg?: string;
  token?: string;
  first_login?: boolean;
  user_id?: string;
  client_id?: string;
}

interface ChangePasswordApiError {
  response?: {
    data?: {
      errors?: string[];
      msg?: string;
    };
  };
}

function InvalidLinkState() {
  const intl = useIntl();
  const { navigate } = useRouting();

  return (
    <>
      <Banner
        status="critical"
        text={intl.formatMessage(messages.invalidLinkMessage)}
        title={intl.formatMessage(messages.invalidLinkTitle)}
      />
      <Layout className={styles.primaryAction}>
        <Button
          label={intl.formatMessage(messages.requestNewLinkButton)}
          text={intl.formatMessage(messages.requestNewLinkButton)}
          variant="primary"
          onClick={() => navigate(RoutePathType.ForgotPassword)}
        />
      </Layout>
    </>
  );
}

function SuccessState() {
  const intl = useIntl();

  return (
    <Banner
      status="positive"
      text={intl.formatMessage(messages.successMessage)}
      title={intl.formatMessage(messages.successTitle)}
    />
  );
}

interface ChangePasswordFormProps {
  slug: string | null;
  token: string;
  tokenType: string | null;
  onSuccess: () => void;
}

function ChangePasswordForm({
  slug,
  token,
  tokenType,
  onSuccess,
}: ChangePasswordFormProps) {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const featureFlags = {
    searchAsHome: useFlag(FlagType.SearchAsHomePage),
  };
  const [apiError, setApiError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      password: '',
      confirmedPassword: '',
    } as ChangePasswordFormValues,
    onSubmit: async ({ value }) => {
      setApiError(null);

      try {
        const response = (await changePassword({
          token,
          slug,
          tokenType,
          formData: { new_password: value.password },
        })) as ChangePasswordResponse;

        await completePasswordResetHandoff(response, {
          featureFlags,
          event: 'User Reset Password',
          formatDate,
        });
        onSuccess();
      } catch (error) {
        const data = (error as ChangePasswordApiError)?.response?.data;
        const message =
          (Array.isArray(data?.errors) &&
            data.errors.length > 0 &&
            data.errors.join(' ')) ||
          data?.msg ||
          intl.formatMessage(messages.genericErrorMessage);
        setApiError(message);
      }
    },
  });

  useFormFocusManager(form);

  const submissionAttempts = useStore(
    form.store,
    (state) => state.submissionAttempts,
  );
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const password = useStore(form.store, (state) => state.values.password);

  return (
    <Layout
      action="#"
      as="form"
      direction="column"
      method="POST"
      noValidate
      spacing="l"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      {apiError && (
        <Banner
          status="critical"
          text={apiError}
          onDismiss={() => setApiError(null)}
        />
      )}

      <Layout className={styles.fieldRow} spacing="m" wrap>
        <form.Field
          name="password"
          validators={{
            onChange: ({ value }) =>
              isPasswordFullyValid(value)
                ? undefined
                : intl.formatMessage(messages.passwordRequirementsError),
            onSubmit: ({ value }) =>
              isPasswordFullyValid(value)
                ? undefined
                : intl.formatMessage(messages.passwordRequirementsError),
          }}
        >
          {(field) => (
            <FormField
              hint={
                submissionAttempts > 0
                  ? (field.state.meta.errors[0] ?? undefined)
                  : undefined
              }
              label={intl.formatMessage(messages.newPasswordLabel)}
              name={field.name}
              status={
                submissionAttempts > 0 && field.state.meta.errors[0]
                  ? 'critical'
                  : undefined
              }
              type="password"
              value={field.state.value}
              onChange={(next) => field.handleChange(next ?? '')}
            />
          )}
        </form.Field>

        <form.Field
          name="confirmedPassword"
          validators={{
            onChangeListenTo: ['password'],
            onChange: ({ fieldApi, value }) =>
              value === fieldApi.form.getFieldValue('password')
                ? undefined
                : intl.formatMessage(messages.passwordsDontMatchError),
            onSubmit: ({ fieldApi, value }) =>
              value === fieldApi.form.getFieldValue('password')
                ? undefined
                : intl.formatMessage(messages.passwordsDontMatchError),
          }}
        >
          {(field) => (
            <FormField
              hint={
                submissionAttempts > 0
                  ? (field.state.meta.errors[0] ?? undefined)
                  : undefined
              }
              label={intl.formatMessage(messages.confirmPasswordLabel)}
              name={field.name}
              status={
                submissionAttempts > 0 && field.state.meta.errors[0]
                  ? 'critical'
                  : undefined
              }
              type="password"
              value={field.state.value}
              onChange={(next) => field.handleChange(next ?? '')}
            />
          )}
        </form.Field>
      </Layout>

      <PasswordRequirementsList password={password} />

      <Layout className={styles.primaryAction}>
        <Button
          isLoading={isSubmitting}
          label={intl.formatMessage(messages.submitButton)}
          text={intl.formatMessage(messages.submitButton)}
          type="submit"
          variant="primary"
        />
      </Layout>
    </Layout>
  );
}

function Page() {
  const intl = useIntl();

  const token = getPageSearchQueryByKey<string | null>(QueryParamType.Token);
  const slug = getPageSearchQueryByKey<string | null>(QueryParamType.Slug);
  const tokenType = getPageSearchQueryByKey<string | null>(
    QueryParamType.StytchTokenType,
  );

  const [isSuccess, setIsSuccess] = useState(false);

  const marketingSubtitle = intl.formatMessage(messages.marketingSubtitle);
  const marketingTitle = intl.formatMessage(messages.marketingTitle);

  return (
    <AuthPageLayout
      marketing={
        <AuthMarketingHeader
          subtitle={marketingSubtitle}
          title={marketingTitle}
        />
      }
      marketingHeader={
        <AuthMarketingHeader
          subtitle={marketingSubtitle}
          title={marketingTitle}
          variant="compact"
        />
      }
    >
      <Layout direction="column" spacing="l">
        <Layout direction="column" spacing="s">
          <Text as="h1" variant="heading-m">
            {intl.formatMessage(messages.title)}
          </Text>
          <Text as="p" tone="subdued" variant="body-m">
            {intl.formatMessage(messages.description)}
          </Text>
        </Layout>

        {!token && <InvalidLinkState />}
        {token && isSuccess && <SuccessState />}
        {token && !isSuccess && (
          <ChangePasswordForm
            slug={slug}
            token={token}
            tokenType={tokenType}
            onSuccess={() => setIsSuccess(true)}
          />
        )}
      </Layout>
    </AuthPageLayout>
  );
}

export default Page;
